import {
  ANALYSIS_PERIOD_OPTIONS,
  IMPRESSION_SHARE_CAP,
  IMPRESSION_SHARE_FLOOR,
  type ConfidenceTier,
  type GoogleBiddingInfo,
  type PlatformStatus,
} from './types';

// Pure per-row helpers the recommendation compute needs. Moved here from the
// page's budget-utils so the page and the API share one copy.

const INACTIVE_STATUS = /PAUSED|DISABLE|DELETE|ARCHIVED|REMOVED|ENDED|COMPLETED|CLOSED/;
const ACTIVE_STATUS = /ACTIVE|ENABLE|IN_PROCESS|WITH_ISSUES|PENDING_REVIEW|LEARNING|LIMITED|DELIVERING/;

/**
 * Normalize a platform status — Meta `effective_status`/`status`
 * (ACTIVE, PAUSED, CAMPAIGN_PAUSED, ADSET_PAUSED, ARCHIVED…), TikTok
 * `CAMPAIGN_STATUS_ENABLE`/`…_DISABLE`, Google `ENABLED`/`PAUSED` (or our
 * ACTIVE/PAUSED mapping), Cube `campaign_status`/`adset_status` — to active
 * vs inactive. Inactive words win over active ones (`CAMPAIGN_PAUSED`
 * contains neither "ACTIVE" nor "ENABLE", but order matters for safety).
 * Unknown or absent values are null: the caller falls back to "has spend in
 * the window", so a status we do not recognize never hides a live row.
 */
export function normalizePlatformStatus(raw: unknown): PlatformStatus | null {
  if (raw === null || raw === undefined) return null;
  const s = String(raw).trim().toUpperCase();
  if (!s) return null;
  if (INACTIVE_STATUS.test(s)) return 'INACTIVE';
  if (ACTIVE_STATUS.test(s)) return 'ACTIVE';
  return null;
}

/**
 * Calendar-day average spend over the window ÷ the platform daily budget —
 * null when the row has no platform budget (its "current budget" IS its
 * average spend, so the ratio would always read 100%) or no window.
 */
export function deliveryRatioFor(periodSpend: number, analysisPeriodDays: number, platformBudget: number): number | null {
  if (platformBudget <= 0 || analysisPeriodDays <= 0) return null;
  return periodSpend / analysisPeriodDays / platformBudget;
}

/**
 * Whole days between a `YYYY-MM-DD` data date and `now`. Returns null for a
 * missing/unparseable date. `now` is injectable for deterministic tests.
 */
export function daysSince(dateStr: string | null | undefined, now: number = Date.now()): number | null {
  if (!dateStr) return null;
  const t = new Date(`${dateStr}T00:00:00Z`).getTime();
  if (isNaN(t)) return null;
  return Math.floor((now - t) / 86_400_000);
}

function shareOrNull(value: unknown): number | null {
  return typeof value === 'number' && Number.isFinite(value) ? value : null;
}

/**
 * Bidding context from a `/paid-media/google/campaigns` row (fields present
 * since the bidding-aware reads landed). Constraint detection uses Google's
 * own `primary_status_reasons` diagnosis: BIDDING_STRATEGY_CONSTRAINED means
 * the tROAS/bid target — not budget — is what limits scale.
 */
export function deriveGoogleBidding(campaign: unknown): GoogleBiddingInfo {
  const c = (campaign ?? {}) as {
    targetRoas?: number;
    biddingStrategyType?: string;
    primaryStatusReasons?: string[];
    portfolioBiddingStrategy?: string;
    searchImpressionShare?: number | null;
    searchBudgetLostShare?: number | null;
    searchRankLostShare?: number | null;
    impressionShareWindow?: GoogleBiddingInfo['impressionShareWindow'];
  };
  const reasons = c.primaryStatusReasons ?? [];
  const info: GoogleBiddingInfo = {
    targetRoas: typeof c.targetRoas === 'number' ? c.targetRoas : undefined,
    biddingStrategyType: c.biddingStrategyType,
    constraint: reasons.includes('BIDDING_STRATEGY_CONSTRAINED') ? 'target' : reasons.includes('BUDGET_CONSTRAINED') ? 'budget' : null,
    learning: reasons.includes('BIDDING_STRATEGY_LEARNING'),
    portfolio: Boolean(c.portfolioBiddingStrategy),
  };
  // The shares are copied only when the live read carried them, so a row
  // from a read that could not fetch them has no share keys (vs null = not
  // applicable), and older payloads keep the exact shape they had.
  if (typeof campaign === 'object' && campaign !== null && 'searchImpressionShare' in campaign) {
    info.searchImpressionShare = shareOrNull(c.searchImpressionShare);
    info.searchBudgetLostShare = shareOrNull(c.searchBudgetLostShare);
    info.searchRankLostShare = shareOrNull(c.searchRankLostShare);
    info.impressionShareWindow = c.impressionShareWindow ?? null;
  }
  return info;
}

/**
 * "62%", or Google's own "<10%" / ">90%" when the value is one of its
 * sentinels (0.0999 / 0.9001 — see IMPRESSION_SHARE_FLOOR / _CAP). Null when
 * there is no share to show.
 */
export function formatImpressionShare(value: number | null | undefined): string | null {
  if (typeof value !== 'number' || !Number.isFinite(value)) return null;
  if (Math.abs(value - IMPRESSION_SHARE_FLOOR) < 1e-6) return '<10%';
  if (Math.abs(value - IMPRESSION_SHARE_CAP) < 1e-6) return '>90%';
  return `${Math.round(value * 100)}%`;
}

/**
 * "IS 62% · lost to budget 30% · lost to rank 8%" for a Google row's bidding
 * info — the one line that says whether a brand campaign's delivery is capped
 * by its budget or by its bids. Null when the row carries no impression
 * share (not Search/Shopping, nothing served, or the read failed), so a
 * caller leaves its surface exactly as it was.
 */
export function formatSearchImpressionShare(
  info: Pick<GoogleBiddingInfo, 'searchImpressionShare' | 'searchBudgetLostShare' | 'searchRankLostShare'> | null | undefined,
): string | null {
  const share = formatImpressionShare(info?.searchImpressionShare);
  if (share === null) return null;
  const parts = [`IS ${share}`];
  const budget = formatImpressionShare(info?.searchBudgetLostShare);
  if (budget !== null) parts.push(`lost to budget ${budget}`);
  const rank = formatImpressionShare(info?.searchRankLostShare);
  if (rank !== null) parts.push(`lost to rank ${rank}`);
  return parts.join(' · ');
}

/** Confidence tier from estimated conversions (null when AOV is unset) */
export function confidenceFor(estimatedConversions: number | null): ConfidenceTier | null {
  if (estimatedConversions === null) return null;
  if (estimatedConversions < 10) return 'LOW';
  if (estimatedConversions <= 30) return 'MEDIUM';
  return 'HIGH';
}

/**
 * Pick a row's analysis period from its estimated conversions per candidate
 * period: the SHORTEST period whose confidence is High. When no period
 * reaches High, the period with the most estimated conversions wins (ties go
 * to the longer window) — the highest confidence available for that row.
 * Null when there is nothing to choose on: AOV unset (every entry null) or
 * no conversions in any window (nothing to evaluate).
 */
export function autoSelectPeriod(
  conversionsByPeriod: Readonly<Record<number, number | null>>,
  periods: readonly number[] = ANALYSIS_PERIOD_OPTIONS,
): number | null {
  let best: { period: number; conversions: number } | null = null;
  for (const period of [...periods].sort((a, b) => a - b)) {
    const conversions = conversionsByPeriod[period];
    if (conversions === null || conversions === undefined) continue;
    if (confidenceFor(conversions) === 'HIGH') return period;
    if (conversions > 0 && (best === null || conversions >= best.conversions)) {
      best = { period, conversions };
    }
  }
  return best?.period ?? null;
}
